// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall, apiCallEx, errorText, viewDocument, openInWord } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";

// EXHIBIT_UI_W3_20260415 — counterparty-specific template source badge + filter
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

// Negotiation-baseline controls for the counterparty revision review engine
// (Phase 1 Wave 1). Rendered as a thin secondary row inside each template
// card header. Toggle requires prior clause ingest; re-ingest replaces the
// existing clause rows atomically.
const NegotiationBaselineRow = ({ template, onChanged }: { template: any; onChanged: () => void }) => {
  const [busy, setBusy] = useState<null | 'ingest' | 'toggle'>(null);
  const [lastResult, setLastResult] = useState<null | string>(null);
  const [err, setErr] = useState<string>("");
  const ingested = !!template.clauses_ingested_at;
  const enabled = !!template.is_negotiation_baseline;

  const ingest = async (isReingest: boolean) => {
    if (isReingest && !window.confirm("Re-ingest will replace all existing clause records for this template and recompute the fingerprint. Continue?")) return;
    setBusy('ingest'); setErr("");
    try {
      const r = await apiCall(`/self-service/templates/${template.id}/ingest-clauses`, { method: "POST" });
      if (r?.clauses_ingested) {
        setLastResult(`${r.clauses_ingested} clause${r.clauses_ingested === 1 ? "" : "s"} ingested`);
        onChanged();
      } else {
        setErr(r?.error || r?.detail || "Ingest returned no clauses.");
      }
    } catch (e: any) {
      setErr(e?.message || "Ingest request failed.");
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (next: boolean) => {
    setBusy('toggle'); setErr("");
    try {
      const r = await apiCall(`/self-service/templates/${template.id}/toggle-negotiation-baseline`, {
        method: "POST",
        body: JSON.stringify({ enabled: next }),
      });
      if (r?.id) {
        onChanged();
      } else {
        setErr(r?.error || "Toggle failed.");
      }
    } catch (e: any) {
      setErr(e?.message || "Toggle request failed.");
    } finally {
      setBusy(null);
    }
  };

  const isDocx = !!(template.file_name && template.file_name.toLowerCase().endsWith('.docx'));
  if (!isDocx) return null; // clause ingest requires .docx

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, fontSize: 11, color: T.txM, flexWrap: "wrap" }}>
      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: busy ? "wait" : "pointer" }} title={!ingested && !enabled ? "Ingest clauses first to enable negotiation-baseline review." : ""}>
        <input
          type="checkbox"
          checked={enabled}
          disabled={!!busy || (!ingested && !enabled)}
          onChange={(e) => toggle(e.target.checked)}
        />
        <span style={{ fontWeight: 600, color: enabled ? T.grn : T.txM }}>
          Negotiation Baseline {enabled ? "on" : "off"}
        </span>
      </label>
      {!ingested && (
        <Btn v="secondary" sz="sm" disabled={!!busy} onClick={() => ingest(false)}>
          {busy === 'ingest' ? "Ingesting..." : "Ingest Clauses"}
        </Btn>
      )}
      {ingested && (
        <>
          <span style={{ fontSize: 10, color: T.txD }}>
            Clauses ingested {fmtDate(template.clauses_ingested_at)}
          </span>
          <a
            href="#"
            style={{ fontSize: 10, color: T.acc, textDecoration: "underline", cursor: busy ? "wait" : "pointer" }}
            onClick={(e) => { e.preventDefault(); if (!busy) ingest(true); }}
          >
            {busy === 'ingest' ? "Re-ingesting..." : "Re-ingest"}
          </a>
        </>
      )}
      {lastResult && <span style={{ fontSize: 10, color: T.grn }}>{lastResult}</span>}
      {err && <span style={{ fontSize: 10, color: T.red }}>{err}</span>}
    </div>
  );
};

// ============================================================
// Delta Harvest Wave 1 components
// ============================================================

const DH_STAGE_LABELS: { value: string; label: string }[] = [
  { value: "v1", label: "v1" },
  { value: "v2", label: "v2" },
  { value: "v3", label: "v3" },
  { value: "v4", label: "v4" },
  { value: "v5", label: "v5" },
  { value: "v6", label: "v6" },
  { value: "v7", label: "v7" },
  { value: "v8", label: "v8" },
  { value: "v9", label: "v9" },
  { value: "v10", label: "v10" },
  { value: "executed", label: "executed" },
];

const DH_STAGE_SEQUENCE: Record<string, number> = {
  v1: 1, v2: 2, v3: 3, v4: 4, v5: 5, v6: 6, v7: 7, v8: 8, v9: 9, v10: 10, executed: 11,
};

// Guess a stage label from a filename. Heuristics: look for explicit v-number
// markers, then executed/final/signed, else pick the next sequential v-stage
// based on what's already uploaded.
function guessStageLabel(fileName: string, alreadyUsed: Set<string>): string {
  const n = (fileName || "").toLowerCase();
  const m = n.match(/\b(v|version\s*|draft\s*)(10|[1-9])\b/);
  if (m) {
    const stage = `v${m[2]}`;
    if (!alreadyUsed.has(stage)) return stage;
  }
  if (/\b(executed|fully-?executed|final|signed|_fe\b|\.fe\b)/.test(n)) {
    if (!alreadyUsed.has("executed")) return "executed";
  }
  for (let i = 1; i <= 10; i++) {
    const candidate = `v${i}`;
    if (!alreadyUsed.has(candidate)) return candidate;
  }
  return "executed";
}

// Delta preview modal. Read-only list of deltas for a deal.
const DeltaPreviewModal = ({ open, onClose, template, deal }: { open: boolean; onClose: () => void; template: any; deal: any }) => {
  const [deltas, setDeltas] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>("");

  useEffect(() => {
    if (!open || !deal) return;
    setLoading(true); setErr("");
    apiCall(`/self-service/templates/${template.id}/dh/deltas/preview?deal_id=${deal.id}&limit=50`)
      .then((r: any) => {
        if (Array.isArray(r?.deltas)) setDeltas(r.deltas);
        else setErr(r?.error || "Failed to load deltas.");
      })
      .catch((e: any) => setErr(e?.message || "Preview request failed."))
      .finally(() => setLoading(false));
  }, [open, deal, template.id]);

  const changeBg = (ct: string) => ({
    modified: T.ylwS,
    inserted: T.grnS,
    deleted: T.redS,
    unchanged_but_commented: T.bg3,
  }[ct] || T.bg3);

  const roleColor = (role: string) => ({
    outside_counsel: T.acc,
    inside_counsel: T.prp,
    business_team: T.blu,
    unknown: T.txD,
  }[role] || T.txD);

  return (
    <Modal open={open} onClose={onClose} title={deal ? `Deltas — ${deal.counterparty_name}` : "Deltas"} w={1000}>
      {loading && <div style={{ padding: 10, color: T.txD, fontSize: 11 }}>Loading...</div>}
      {err && <div style={{ padding: 10, color: T.red, fontSize: 11 }}>{err}</div>}
      {!loading && !err && deltas.length === 0 && (
        <div style={{ padding: 20, textAlign: "center", color: T.txD, fontSize: 11 }}>No deltas yet. Run Extract first.</div>
      )}
      <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {deltas.map((d: any) => {
          const comments = Array.isArray(d.harvested_comments) ? d.harvested_comments : [];
          return (
            <div key={d.id} style={{ marginBottom: 12, padding: 10, border: `1px solid ${T.brd}`, borderRadius: 6, backgroundColor: T.bg2 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
                <div style={{ fontSize: 11, color: T.txM }}>
                  <strong style={{ color: T.tx }}>{d.from_stage} → {d.to_stage}</strong>
                  {d.heading_path && <span style={{ marginLeft: 8, color: T.txD }}> · {d.heading_path.length > 80 ? d.heading_path.slice(0, 80) + "..." : d.heading_path}</span>}
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <Badge c={T.tx} b={changeBg(d.change_type)}>{String(d.change_type || "").replace(/_/g, " ")}</Badge>
                  {d.survived_to_executed === true && <Badge c={T.grn} b={T.grnS}>survived</Badge>}
                  {d.survived_to_executed === false && <Badge c={T.red} b={T.redS}>did not survive</Badge>}
                  {d.survived_to_executed == null && <Badge c={T.txD} b={T.bg4}>survival N/A</Badge>}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div style={{ padding: 8, backgroundColor: d.before_text ? T.redS : T.bg3, borderRadius: 4, fontSize: 11, color: T.tx, whiteSpace: "pre-wrap" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: T.txD, marginBottom: 4, textTransform: "uppercase" }}>Before ({d.from_stage})</div>
                  {d.before_text || <span style={{ color: T.txD, fontStyle: "italic" }}>(not present)</span>}
                </div>
                <div style={{ padding: 8, backgroundColor: d.after_text ? T.grnS : T.bg3, borderRadius: 4, fontSize: 11, color: T.tx, whiteSpace: "pre-wrap" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: T.txD, marginBottom: 4, textTransform: "uppercase" }}>After ({d.to_stage})</div>
                  {d.after_text || <span style={{ color: T.txD, fontStyle: "italic" }}>(not present)</span>}
                </div>
              </div>
              {comments.length > 0 && (
                <div style={{ marginTop: 8, padding: 6, backgroundColor: T.bg3, borderRadius: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 4, textTransform: "uppercase" }}>Comments ({comments.length})</div>
                  {comments.map((c: any, i: number) => (
                    <div key={i} style={{ fontSize: 10, color: T.tx, padding: "3px 0", borderTop: i > 0 ? `1px solid ${T.brdSubtle}` : "none" }}>
                      <Badge c={roleColor(c.author_role)} b={T.bg2}>{String(c.author_role || "unknown").replace(/_/g, " ")}</Badge>
                      <strong style={{ marginLeft: 6, color: T.txM }}>{c.author || "unknown"}</strong>
                      <span style={{ marginLeft: 4, color: T.txD, fontSize: 9 }}>({c.source_draft_stage})</span>
                      <div style={{ marginTop: 2, color: T.tx }}>{c.comment_text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};


// Upload surface rendered inside each deal's detail expansion.
const DealUploadSurface = ({ template, deal, onChanged }: { template: any; deal: any; onChanged: () => void }) => {
  const [drafts, setDrafts] = useState<any[]>([]);
  const [queue, setQueue] = useState<{ file: File; stage: string; status: string; err?: string }[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [deal_, setDeal_] = useState<any>(deal);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadDetail = useCallback(async () => {
    const r = await apiCall(`/self-service/templates/${template.id}/dh/deals/${deal.id}`);
    if (r?.drafts) setDrafts(r.drafts);
    if (r?.deal) setDeal_(r.deal);
  }, [template.id, deal.id]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  // Poll while extracting.
  useEffect(() => {
    if (deal_?.ingest_status !== "extracting") return;
    const t = setInterval(async () => { await loadDetail(); onChanged(); }, 5000);
    return () => clearInterval(t);
  }, [deal_?.ingest_status, loadDetail, onChanged]);

  const addFilesToQueue = (files: FileList | File[]) => {
    const arr = Array.from(files).filter((f: any) => f.name.toLowerCase().endsWith(".docx"));
    setQueue(prev => {
      const alreadyUsed = new Set<string>([...drafts.map(d => d.stage_label), ...prev.map(p => p.stage)]);
      const additions = arr.map((f: any) => {
        const stage = guessStageLabel(f.name, alreadyUsed);
        alreadyUsed.add(stage);
        return { file: f, stage, status: "queued" };
      });
      return [...prev, ...additions];
    });
  };

  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = () => setDragActive(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) addFilesToQueue(e.dataTransfer.files);
  };

  const updateQueueStage = (idx: number, stage: string) => {
    setQueue(prev => prev.map((q, i) => i === idx ? { ...q, stage } : q));
  };

  const removeFromQueue = (idx: number) => {
    setQueue(prev => prev.filter((_, i) => i !== idx));
  };

  const uploadAll = async () => {
    if (queue.length === 0) return;
    setUploading(true);
    const token = localStorage.getItem("kontractai_token");
    // Upload in stage order for a deterministic sequence.
    const indexed = queue.map((q, i) => ({ ...q, _idx: i }));
    indexed.sort((a, b) => (DH_STAGE_SEQUENCE[a.stage] || 99) - (DH_STAGE_SEQUENCE[b.stage] || 99));
    for (const item of indexed) {
      setQueue(prev => prev.map((q, i) => i === item._idx ? { ...q, status: "uploading", err: undefined } : q));
      try {
        const fd = new FormData();
        fd.append("stage_label", item.stage);
        fd.append("file", item.file);
        const res = await fetch(`/api/self-service/templates/${template.id}/dh/deals/${deal.id}/drafts`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (res.ok) {
          setQueue(prev => prev.map((q, i) => i === item._idx ? { ...q, status: "uploaded" } : q));
        } else {
          const e = await res.json().catch(() => ({}));
          setQueue(prev => prev.map((q, i) => i === item._idx ? { ...q, status: "failed", err: e?.error || `HTTP ${res.status}` } : q));
        }
      } catch (e: any) {
        setQueue(prev => prev.map((q, i) => i === item._idx ? { ...q, status: "failed", err: e?.message || "network error" } : q));
      }
    }
    setUploading(false);
    // Prune successfully-uploaded items from the queue.
    setQueue(prev => prev.filter(q => q.status !== "uploaded"));
    await loadDetail();
    onChanged();
  };

  const deleteDraft = async (draftId: string, stage: string) => {
    // No single-draft DELETE endpoint in Wave 1; operator deletes the whole
    // deal instead if they need to redo. Show a hint instead of erroring.
    alert(`To remove a draft, delete the whole deal and recreate. (Single-draft delete is a Phase 2 feature.)`);
  };

  const extract = async () => {
    setExtracting(true);
    try {
      const r = await apiCall(`/self-service/templates/${template.id}/dh/deals/${deal.id}/extract`, { method: "POST" });
      if (r?.status === "extracting") { await loadDetail(); onChanged(); }
      else alert(r?.error || r?.detail || "Extract failed");
    } catch (e: any) { alert(e?.message || "Extract request failed."); }
    finally { setExtracting(false); }
  };

  const canExtract = drafts.length >= 2 && deal_?.ingest_status !== "extracting";

  return (
    <div style={{ padding: "10px 14px 14px", borderTop: `1px solid ${T.brdSubtle}`, backgroundColor: T.bg3 }}>
      {/* Drop zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragActive ? T.acc : T.brd}`,
          borderRadius: 8,
          padding: 18,
          textAlign: "center",
          backgroundColor: dragActive ? T.bg4 : T.bg2,
          transition: "background-color .15s, border-color .15s",
          cursor: "pointer",
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>Drop draft files here (DOCX only)</div>
        <div style={{ fontSize: 10, color: T.txD, marginTop: 4 }}>or click to browse</div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx"
          multiple
          onChange={e => e.target.files && addFilesToQueue(e.target.files)}
          style={{ display: "none" }}
        />
      </div>

      {/* Queue */}
      {queue.length > 0 && (
        <div style={{ marginTop: 10, backgroundColor: T.bg2, borderRadius: 6, padding: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.txM, marginBottom: 6 }}>Queued ({queue.length})</div>
          {queue.map((q, i) => {
            const alreadyUsed = new Set<string>([...drafts.map(d => d.stage_label), ...queue.filter((x, xi) => xi !== i).map(x => x.stage)]);
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1.5fr 100px 1fr auto", gap: 6, padding: "4px 0", fontSize: 11, alignItems: "center", borderBottom: `1px solid ${T.brdSubtle}` }}>
                <span style={{ color: T.tx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.file.name}</span>
                <select
                  value={q.stage}
                  onChange={e => updateQueueStage(i, e.target.value)}
                  disabled={q.status === "uploading" || q.status === "uploaded"}
                  style={{ padding: "4px 6px", fontSize: 11, border: `1px solid ${T.brd}`, borderRadius: 4, backgroundColor: T.bg2, color: T.tx }}
                >
                  {DH_STAGE_LABELS.map(s => (
                    <option key={s.value} value={s.value} disabled={alreadyUsed.has(s.value)}>{s.label}{alreadyUsed.has(s.value) ? " (used)" : ""}</option>
                  ))}
                </select>
                <span style={{ fontSize: 10, color: q.status === "failed" ? T.red : q.status === "uploaded" ? T.grn : T.txM }}>
                  {q.status}{q.err ? `: ${q.err}` : ""}
                </span>
                {q.status !== "uploaded" && (
                  <Btn v="ghost" sz="sm" onClick={() => removeFromQueue(i)} disabled={q.status === "uploading"}>Remove</Btn>
                )}
              </div>
            );
          })}
          <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end", gap: 6 }}>
            <Btn v="secondary" sz="sm" onClick={() => setQueue([])} disabled={uploading}>Clear Queue</Btn>
            <Btn v="primary" sz="sm" onClick={uploadAll} disabled={uploading || queue.length === 0}>
              {uploading ? "Uploading..." : `Upload All (${queue.length})`}
            </Btn>
          </div>
        </div>
      )}

      {/* Already-uploaded drafts */}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.txM, marginBottom: 6 }}>Uploaded Drafts ({drafts.length})</div>
        {drafts.length === 0 ? (
          <div style={{ padding: 10, textAlign: "center", fontSize: 10, color: T.txD, backgroundColor: T.bg2, borderRadius: 4 }}>No drafts yet.</div>
        ) : (
          <div style={{ backgroundColor: T.bg2, borderRadius: 6 }}>
            {drafts.map((d: any) => (
              <div key={d.id} style={{ display: "grid", gridTemplateColumns: "70px 2fr 1fr 1fr", gap: 8, padding: "6px 10px", fontSize: 11, color: T.tx, borderBottom: `1px solid ${T.brdSubtle}`, alignItems: "center" }}>
                <Badge c={T.tx} b={T.bg3}>{d.stage_label}</Badge>
                <span style={{ color: T.txM, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.file_name}</span>
                <span style={{ color: T.txD, fontSize: 10 }}>{d.file_size ? `${Math.round(d.file_size / 1024)} KB` : "—"}</span>
                <span style={{ color: T.txD, fontSize: 10 }}>{d.uploaded_at ? fmtDate(d.uploaded_at) : "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Extract + status */}
      <div style={{ marginTop: 10, padding: 8, backgroundColor: T.bg2, borderRadius: 4, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: T.txM }}>
          Status: <Badge c={deal_?.ingest_status === "failed" ? T.red : deal_?.ingest_status === "extracted" ? T.grn : deal_?.ingest_status === "extracting" ? T.ylw : T.txM} b={T.bg4}>{deal_?.ingest_status || "awaiting_drafts"}</Badge>
          {deal_?.ingest_error && <span style={{ marginLeft: 8, color: T.red, fontSize: 10 }}> · {deal_.ingest_error}</span>}
        </span>
        <Btn v="primary" sz="sm" disabled={!canExtract || extracting} onClick={extract}>
          {deal_?.ingest_status === "extracting" ? "Extracting..." : deal_?.ingest_status === "extracted" ? "Re-extract" : "Extract Deltas"}
        </Btn>
      </div>
    </div>
  );
};

// Main DH panel rendered below each negotiation-baseline template row.
const DeltaHarvestPanel = ({ template }: { template: any }) => {
  const storageKey = `kontractai_dh_expanded_${template.id}`;
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const [deals, setDeals] = useState<any[]>([]);
  const [loadErr, setLoadErr] = useState<string>("");
  const [showNewDealModal, setShowNewDealModal] = useState(false);
  const [newDealCp, setNewDealCp] = useState("");
  const [newDealDate, setNewDealDate] = useState("");
  const [newDealNotes, setNewDealNotes] = useState("");
  const [submittingDeal, setSubmittingDeal] = useState(false);
  const [newDealErr, setNewDealErr] = useState("");
  const [expandedDealId, setExpandedDealId] = useState<string | null>(null);
  const [previewDeal, setPreviewDeal] = useState<any | null>(null);

  useEffect(() => {
    try { localStorage.setItem(storageKey, expanded ? "1" : "0"); } catch {}
  }, [expanded, storageKey]);

  const loadDeals = useCallback(async () => {
    setLoadErr("");
    try {
      const r = await apiCall(`/self-service/templates/${template.id}/dh/deals`);
      if (Array.isArray(r?.deals)) setDeals(r.deals);
      else setLoadErr(r?.error || "Failed to load deals.");
    } catch (e: any) { setLoadErr(e?.message || "Deals request failed."); }
  }, [template.id]);

  useEffect(() => { if (expanded) loadDeals(); }, [expanded, loadDeals]);

  // Poll list while any deal is extracting.
  useEffect(() => {
    const active = deals.some(d => d.ingest_status === "extracting");
    if (!active || !expanded) return;
    const t = setInterval(loadDeals, 5000);
    return () => clearInterval(t);
  }, [deals, expanded, loadDeals]);

  const submitNewDeal = async () => {
    if (!newDealCp.trim()) { setNewDealErr("Counterparty name is required."); return; }
    setSubmittingDeal(true); setNewDealErr("");
    try {
      const body: any = { counterparty_name: newDealCp.trim() };
      if (newDealDate) body.execution_date = newDealDate;
      if (newDealNotes.trim()) body.deal_notes = newDealNotes.trim();
      const r = await apiCall(`/self-service/templates/${template.id}/dh/deals`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (r?.deal_id) {
        setNewDealCp(""); setNewDealDate(""); setNewDealNotes("");
        setShowNewDealModal(false);
        await loadDeals();
      } else setNewDealErr(r?.error || r?.message || "Create deal failed.");
    } catch (e: any) { setNewDealErr(e?.message || "Create deal request failed."); }
    finally { setSubmittingDeal(false); }
  };

  const deleteDeal = async (dealId: string, cpName: string) => {
    if (!window.confirm(`Delete deal "${cpName}"? This cascades to all uploaded drafts, deltas, and files on disk.`)) return;
    try {
      const r = await apiCall(`/self-service/templates/${template.id}/dh/deals/${dealId}`, { method: "DELETE" });
      if (r?.deleted) {
        if (expandedDealId === dealId) setExpandedDealId(null);
        await loadDeals();
      } else alert(r?.error || "Delete failed");
    } catch (e: any) { alert(e?.message || "Delete request failed."); }
  };

  // Overview aggregates from the deal list.
  const totalDeals = deals.length;
  const byStatus: Record<string, number> = {};
  let totalDeltas = 0;
  for (const d of deals) {
    byStatus[d.ingest_status] = (byStatus[d.ingest_status] || 0) + 1;
    totalDeltas += (d.delta_count || 0);
  }
  const extractedCount = byStatus["extracted"] || 0;
  const extractingCount = byStatus["extracting"] || 0;
  const awaitingCount = byStatus["awaiting_drafts"] || 0;
  const failedCount = byStatus["failed"] || 0;

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ margin: "10px 16px 12px", border: `1px solid ${T.brd}`, borderRadius: 8, backgroundColor: T.bg3 }}>
      <div
        onClick={() => setExpanded(x => !x)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", cursor: "pointer" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.tx }}>Delta Harvest</span>
          <Badge c={T.acc} b={T.bg4}>Wave 1</Badge>
          {totalDeals > 0 && <span style={{ fontSize: 11, color: T.txM }}>{totalDeals} deal{totalDeals === 1 ? "" : "s"} · {totalDeltas} delta{totalDeltas === 1 ? "" : "s"}</span>}
        </div>
        <span style={{ fontSize: 11, color: T.txD, transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}>&#9660;</span>
      </div>

      {expanded && (
        <div style={{ padding: "8px 14px 14px", borderTop: `1px solid ${T.brd}` }}>
          {loadErr && <div style={{ fontSize: 11, color: T.red, marginBottom: 8 }}>{loadErr}</div>}

          {/* Overview */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 12 }}>
            {[
              { label: "Deals", val: totalDeals, c: T.acc },
              { label: "Extracted", val: extractedCount, c: T.grn },
              { label: "Extracting", val: extractingCount, c: T.ylw },
              { label: "Awaiting", val: awaitingCount, c: T.txM },
              { label: "Failed", val: failedCount, c: failedCount > 0 ? T.red : T.txD },
            ].map(m => (
              <div key={m.label} style={{ padding: 10, backgroundColor: T.bg2, borderRadius: 6, textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: m.c }}>{m.val}</div>
                <div style={{ fontSize: 9, color: T.txD, marginTop: 2, textTransform: "uppercase" }}>{m.label}</div>
              </div>
            ))}
          </div>

          {/* Action bar */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <Btn v="primary" sz="sm" onClick={() => setShowNewDealModal(true)}>+ New Deal</Btn>
            <span style={{ fontSize: 10, color: T.txD }}>Total deltas harvested: <strong style={{ color: T.tx }}>{totalDeltas}</strong></span>
          </div>

          {/* Deal list */}
          {deals.length === 0 ? (
            <div style={{ padding: 14, textAlign: "center", fontSize: 11, color: T.txD, backgroundColor: T.bg2, borderRadius: 6 }}>
              No deals yet. Click New Deal to begin.
            </div>
          ) : (
            <div style={{ backgroundColor: T.bg2, borderRadius: 6, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1.2fr 70px 70px 1fr auto auto auto", gap: 6, padding: "8px 12px", fontSize: 10, fontWeight: 700, color: T.txD, textTransform: "uppercase", borderBottom: `1px solid ${T.brd}` }}>
                <span>Counterparty</span><span>Execution</span><span>Status</span><span>Drafts</span><span>Deltas</span><span>Created</span><span></span><span></span><span></span>
              </div>
              {deals.map((d: any) => {
                const isExp = expandedDealId === d.id;
                return (
                  <div key={d.id} style={{ borderBottom: `1px solid ${T.brdSubtle}` }}>
                    <div
                      onClick={() => setExpandedDealId(isExp ? null : d.id)}
                      style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1.2fr 70px 70px 1fr auto auto auto", gap: 6, padding: "8px 12px", fontSize: 11, color: T.tx, cursor: "pointer", alignItems: "center" }}
                    >
                      <span style={{ fontWeight: 600 }}>{d.counterparty_name}</span>
                      <span style={{ color: T.txM }}>{d.execution_date ? fmtDate(d.execution_date) : "—"}</span>
                      <span>
                        <Badge c={d.ingest_status === "failed" ? T.red : d.ingest_status === "extracted" ? T.grn : d.ingest_status === "extracting" ? T.ylw : T.txM} b={T.bg4}>{String(d.ingest_status || "").replace(/_/g, " ")}</Badge>
                      </span>
                      <span style={{ color: T.txM }}>{d.draft_count || 0}</span>
                      <span style={{ color: d.delta_count > 0 ? T.grn : T.txM, fontWeight: d.delta_count > 0 ? 700 : 400 }}>{d.delta_count || 0}</span>
                      <span style={{ color: T.txD, fontSize: 10 }}>{d.created_at ? fmtDate(d.created_at) : "—"}</span>
                      {d.ingest_status === "extracted" ? (
                        <span onClick={e => { e.stopPropagation(); setPreviewDeal(d); }} style={{ fontSize: 10, color: T.acc, textDecoration: "underline", cursor: "pointer", padding: "0 4px" }}>View Deltas</span>
                      ) : <span />}
                      <span onClick={e => { e.stopPropagation(); deleteDeal(d.id, d.counterparty_name); }} title="Delete deal" style={{ color: T.red, fontSize: 14, cursor: "pointer", padding: "0 4px" }}>🗑</span>
                      <span style={{ color: T.txD, fontSize: 10, transform: isExp ? "rotate(180deg)" : "none", transition: "transform .2s" }}>&#9660;</span>
                    </div>
                    {isExp && (
                      <DealUploadSurface template={template} deal={d} onChanged={loadDeals} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <DeltaPreviewModal open={!!previewDeal} onClose={() => setPreviewDeal(null)} template={template} deal={previewDeal} />

      <Modal open={showNewDealModal} onClose={() => { if (!submittingDeal) setShowNewDealModal(false); }} title="New Delta Harvest Deal" w={520}>
        <div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 4 }}>Counterparty Name *</label>
            <Inp value={newDealCp} onChange={setNewDealCp} placeholder="e.g. Acme Pharmaceuticals, Inc." />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 4 }}>Execution Date</label>
            <Inp type="date" value={newDealDate} onChange={setNewDealDate} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 4 }}>Deal Notes</label>
            <textarea value={newDealNotes} onChange={e => setNewDealNotes(e.target.value)} rows={3} style={{ width: "100%", padding: 8, border: `1px solid ${T.brd}`, borderRadius: 6, backgroundColor: T.bg2, color: T.tx, fontFamily: "inherit", fontSize: 12, resize: "vertical", boxSizing: "border-box" }} placeholder="Optional context..." />
          </div>
          {newDealErr && <div style={{ fontSize: 11, color: T.red, marginBottom: 10 }}>{newDealErr}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Btn v="secondary" onClick={() => setShowNewDealModal(false)} disabled={submittingDeal}>Cancel</Btn>
            <Btn v="primary" onClick={submitNewDeal} disabled={submittingDeal || !newDealCp.trim()}>
              {submittingDeal ? "Creating..." : "Create Deal"}
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
};


export const SelfServiceTemplateManager = () => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [drag, setDrag] = useState(false);
  const [analyzing, setAnalyzing] = useState<any>(null); // template id being analyzed
  const [pendingReview, setPendingReview] = useState<any>(null); // { templateId, ai_result, type_exists }
  const [reviewForm, setReviewForm] = useState<Record<string, any>>({});
  const [newTypeName, setNewTypeName] = useState("");
  const [addingType, setAddingType] = useState(false);
  const [expandedId, setExpandedId] = useState<any>(null);
  const [editId, setEditId] = useState<any>(null);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [contractTypes, setContractTypes] = useState<any>(null);
  const [versionFile, setVersionFile] = useState<any>(null);
  const [versionUploading, setVersionUploading] = useState<any>(null);
  const [spSyncing, setSpSyncing] = useState<any>(null);
  const [scanningPlaceholders, setScanningPlaceholders] = useState<any>(null);
  const [placeholderError, setPlaceholderError] = useState<string | null>(null);
  const [editingPlaceholders, setEditingPlaceholders] = useState<any>(null); // template id
  const [placeholderDraft, setPlaceholderDraft] = useState<any[]>([]);
  const [sourceFilter, setSourceFilter] = useState<"all" | "admin" | "counterparty">("all");
  const [contractsMap, setContractsMap] = useState<Record<string, any>>({});
  const versionInputRef = useRef<any>(null);

  const FIELD_OPTIONS = [
    { value: "company_name", label: "Company Name" },
    { value: "company_address", label: "Company Address" },
    { value: "company_entity_type", label: "Company Entity Type" },
    { value: "counterparty_name", label: "Counterparty Name" },
    { value: "counterparty_address", label: "Counterparty Address" },
    { value: "counterparty_entity_type", label: "Counterparty Entity Type" },
    { value: "counterparty_jurisdiction", label: "Counterparty Jurisdiction" },
    { value: "effective_date", label: "Effective Date" },
    { value: "company_signer_name", label: "Company Signer Name" },
    { value: "company_signer_title", label: "Company Signer Title" },
    { value: "counterparty_signer_name", label: "Counterparty Signer Name" },
    { value: "counterparty_signer_title", label: "Counterparty Signer Title" },
    { value: "state", label: "Governing Law State" },
  ];

  // AI Scan and Save both used bare apiCall(), which returns null on ANY
  // non-2xx, and both call sites then ignored it. The consequence, reported from
  // Certara on 2026-10-07: the owner uploaded a confidentiality agreement, ran
  // AI Scan, saved the placeholders, and the template still read
  // "Placeholder Fields (0 mapped)" — with no error anywhere on the screen.
  //
  // Every outcome below used to look identical to the user: a 502 from the
  // model, a 500, a 403 from the permission check, a 404 from a tenant-context
  // miss on the multi-tenant edition, and a scan whose answer could not be
  // parsed (the route returns {placeholders: [], raw} for that). Worse, the last
  // one OPENED AN EMPTY EDITOR, so pressing Save then wrote an empty list over
  // whatever was already mapped — the silent-write class (PR #550) in the UI
  // layer rather than the database layer.
  //
  // The save path itself is sound, and that was verified rather than assumed:
  // against a fresh single-customer database the PUT returns 200, three
  // placeholders are written and three are read back. So the fix is not to the
  // write; it is to stop discarding the answer.
  const scanPlaceholders = async (t: any) => {
    setScanningPlaceholders(t.id);
    const r = await apiCallEx(`/self-service/templates/${t.id}/scan-placeholders`, { method: "POST" });
    setScanningPlaceholders(null);
    if (!r.ok) {
      setPlaceholderError(r.status === 502
        ? "The AI scan could not be completed. Check the AI provider settings and try again."
        : `The AI scan failed (status ${r.status}). Nothing has been changed.`);
      return;
    }
    const found = Array.isArray(r.data?.placeholders) ? r.data.placeholders : [];
    if (found.length === 0) {
      // Distinguish "the model answered and we could not read it" from "there is
      // genuinely nothing here". The route sets `raw` only in the first case.
      setPlaceholderError(r.data?.raw
        ? "The AI scan ran but its answer could not be read, so no placeholders were loaded. Your saved placeholders are unchanged - try the scan again, or add fields by hand."
        : "The AI scan found no placeholders in this template. Your saved placeholders are unchanged - you can add fields by hand with + Add Field.");
      return; // do NOT open an empty editor over existing work
    }
    setPlaceholderError(null);
    setPlaceholderDraft(found);
    setEditingPlaceholders(t.id);
  };

  const savePlaceholders = async (id: string, existingCount: number) => {
    // Saving an empty list over mapped fields is almost always an accident - it
    // is exactly what an unreported failed scan used to cause. Ask first.
    if (placeholderDraft.length === 0 && existingCount > 0) {
      if (!window.confirm(`This will remove all ${existingCount} saved placeholder fields from this template. Continue?`)) return;
    }
    const r = await apiCallEx(`/self-service/templates/${id}`, { method: "PUT", body: JSON.stringify({ placeholder_fields: placeholderDraft }) });
    if (!r.ok) {
      // Keep the editor open and the draft intact: the user's mapping work must
      // survive a failed save, which is the other half of why this was so
      // expensive - the editor closed and the work was gone.
      // A 403 is NOT necessarily a permission problem — a customer's network
      // gateway returns the same status, and errorText() is the one place that
      // knows the difference. Only the genuinely KontractAI-specific wordings
      // are kept here; everything else defers to the shared helper.
      setPlaceholderError(
        r.upstream ? errorText(r, "")
        : r.status === 403
          ? "You do not have permission to change this template's placeholder fields."
          : r.status === 404
            ? "This template could not be found to update. Reload the page and try again."
            : `Saving the placeholder fields failed (status ${r.status}). Your changes are still on screen - nothing was saved.`);
      return;
    }
    // Trust the row the server returns, not the request we sent.
    const saved = Array.isArray(r.data?.template?.placeholder_fields) ? r.data.template.placeholder_fields : [];
    if (saved.length !== placeholderDraft.length) {
      setPlaceholderError(`The server saved ${saved.length} of ${placeholderDraft.length} placeholder fields. Reload the page to see what was stored.`);
      return;
    }
    setPlaceholderError(null);
    setEditingPlaceholders(null);
    load();
  };

  const load = async () => {
    const data = await apiCall("/self-service/templates");
    if (data?.templates) setTemplates(data.templates);
    const ct = await apiCall("/contract-types");
    if (ct) setContractTypes(ct);
    // Load executed_contracts to resolve source_contract_id -> counterparty_entity for the source badge
    try {
      const contracts = await apiCall("/contracts");
      if (contracts?.flat) {
        const map: Record<string, any> = {};
        for (const c of contracts.flat) map[c.id] = c;
        setContractsMap(map);
      }
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const allTypes = contractTypes?.types ? contractTypes.types.map((t: any) => t.name) : [];
  const typesByCategory = (cat: string) => {
    if (!cat || !contractTypes?.grouped) return [];
    return (contractTypes.grouped[cat] || []).map((t: any) => t.name);
  };

  const uploadAndAnalyze = async (file: File) => {
    const token = localStorage.getItem("kontractai_token");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch("/api/self-service/templates", {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); alert(e.error || "Upload failed"); return; }
      const data = await res.json();
      const templateId = data.template?.id;
      await load();
      if (templateId) {
        setAnalyzing(templateId);
        const r = await apiCall(`/self-service/templates/${templateId}/analyze`, { method: "POST" });
        setAnalyzing(null);
        if (r?.ai_result) {
          setPendingReview({ templateId, ai_result: r.ai_result, type_exists: r.type_exists, document_text: r.document_text || "" });
          setReviewForm({ name: r.ai_result.name || "", agreement_type: r.ai_result.agreement_type || "", agreement_category: r.ai_result.agreement_category || "", description: r.ai_result.description || "" });
          setPlaceholderDraft(r.placeholders || []);
        } else {
          setPendingReview({ templateId, ai_result: null, type_exists: false, document_text: r?.document_text || "" });
          setReviewForm({ name: file.name.replace(/\.(pdf|docx|doc)$/i, ""), agreement_type: "", agreement_category: "", description: "" });
          setPlaceholderDraft(r?.placeholders || []);
        }
      }
    } catch (e: any) { alert("Upload failed"); setAnalyzing(null); }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); setDrag(false);
    const files = Array.from(e.dataTransfer?.files || []).filter((f: any) => f.name.match(/\.(pdf|docx|doc)$/i));
    if (files.length > 0) uploadAndAnalyze(files[0]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) uploadAndAnalyze(file);
    e.target.value = "";
  };

  // Confirm the pending review — save metadata and activate
  const confirmReview = async () => {
    if (!pendingReview || !reviewForm.name || !reviewForm.agreement_type || !reviewForm.agreement_category) return;
    // Last line of defence against the wipe. Even with reAnalyze fixed, an
    // empty draft submitted over a template that HAS mapped fields is almost
    // never what the user means, so it is confirmed rather than assumed.
    const existing = templates.find((t: any) => t.id === pendingReview.templateId);
    const existingCount = Array.isArray(existing?.placeholder_fields) ? existing.placeholder_fields.length : 0;
    if (placeholderDraft.length === 0 && existingCount > 0) {
      if (!window.confirm(`This will remove all ${existingCount} saved placeholder fields from this template. Continue?`)) return;
    }
    const r = await apiCallEx(`/self-service/templates/${pendingReview.templateId}`, {
      method: "PUT",
      body: JSON.stringify({ ...reviewForm, is_active: true, placeholder_fields: placeholderDraft })
    });
    if (!r.ok) {
      // Keep the card open so the user's work is not lost to a failed save.
      // The reported Certara failure landed here: the 403 was the Azure
      // Application Gateway's block page, not KontractAI's, so "you do not have
      // permission" was the one thing that was certainly untrue. errorText()
      // decides from the response body which of the two it is.
      setPlaceholderError(
        r.upstream ? errorText(r, "")
        : r.status === 403
          ? "You do not have permission to activate this template."
          : r.status === 404
            ? "This template could not be found to update. Reload the page and try again."
            : `Activating this template failed (status ${r.status}). Your changes are still on screen — nothing was saved.`);
      return;
    }
    setPlaceholderError(null);
    setPendingReview(null);
    setReviewForm({});
    setNewTypeName("");
    setPlaceholderDraft([]);
    load();
  };

  // Discard the pending template
  const discardPending = async () => {
    if (!pendingReview) return;
    await apiCall(`/self-service/templates/${pendingReview.templateId}`, { method: "DELETE" });
    setPendingReview(null);
    setReviewForm({});
    load();
  };

  // Add a new contract type to the system
  const addNewType = async () => {
    if (!newTypeName || !reviewForm.agreement_category) return;
    setAddingType(true);
    const r = await apiCall("/contract-types", {
      method: "POST",
      body: JSON.stringify({ name: newTypeName, category: reviewForm.agreement_category })
    });
    setAddingType(false);
    if (r) {
      setReviewForm((p: any) => ({ ...p, agreement_type: newTypeName }));
      setNewTypeName("");
      // Reload contract types
      const ct = await apiCall("/contract-types");
      if (ct) setContractTypes(ct);
    } else {
      alert("Failed to add contract type");
    }
  };

  // Re-analyse an existing template. THREE DEFECTS LIVED HERE, and together they
  // destroyed saved work:
  //
  //   1. It opened the review card WITHOUT setting placeholderDraft. The card's
  //      only action, "Confirm & Activate Template", submits placeholderDraft —
  //      so on a fresh page load the draft was [] and confirming wrote an empty
  //      list over every mapped field. Reproduced in a browser against the
  //      deployed build on 2026-10-08: a template showing "Placeholder Fields
  //      (13 mapped)" → Re-analyze with AI → the card reads "No placeholders
  //      detected" → Confirm → "Placeholder Fields (0 mapped)". Silent data loss,
  //      and an exact match for the owner's report from Certara.
  //   2. It dropped `document_text`, which uploadAndAnalyze does pass, so the
  //      highlighted-placeholder preview silently disappeared on this path.
  //   3. When `ai_result` came back falsy — a 502 from the model, a 500, a
  //      parse failure — it did NOTHING AT ALL: no card, no message. The button
  //      appeared to do nothing, which is the "no Save option appeared" half of
  //      the report.
  //
  // The draft now always has a defensible value: what the scan found, else what
  // is already saved on the template. Never empty by accident.
  const reAnalyze = async (t: any) => {
    setAnalyzing(t.id);
    const r = await apiCallEx(`/self-service/templates/${t.id}/analyze`, { method: "POST" });
    setAnalyzing(null);
    if (!r.ok) {
      setPlaceholderError(r.status === 502
        ? "The AI could not analyse this template. Check the AI provider settings and try again. Nothing has been changed."
        : `Re-analysing this template failed (status ${r.status}). Nothing has been changed.`);
      return;
    }
    const d = r.data || {};
    if (!d.ai_result) {
      setPlaceholderError("The AI returned no result for this template, so nothing was changed. Try again, or edit the details by hand.");
      return;
    }
    const scanned = Array.isArray(d.placeholders) ? d.placeholders : [];
    const saved = Array.isArray(t.placeholder_fields) ? t.placeholder_fields : [];
    setPlaceholderError(null);
    setPendingReview({ templateId: t.id, ai_result: d.ai_result, type_exists: d.type_exists, document_text: d.document_text || "" });
    setReviewForm({ name: d.ai_result.name || t.name, agreement_type: d.ai_result.agreement_type || t.agreement_type, agreement_category: d.ai_result.agreement_category || t.agreement_category || "", description: d.ai_result.description || t.description || "" });
    // Prefer what this scan found; fall back to what is already saved rather
    // than to nothing. An empty draft here is how the fields got wiped.
    setPlaceholderDraft(scanned.length > 0 ? scanned : saved);
  };

  const saveEdit = async (id: string) => {
    await apiCall(`/self-service/templates/${id}`, { method: "PUT", body: JSON.stringify(editForm) });
    setEditId(null);
    load();
  };

  const toggleActive = async (t: any) => {
    await apiCall(`/self-service/templates/${t.id}`, { method: "PUT", body: JSON.stringify({ is_active: !t.is_active }) });
    load();
  };

  const uploadVersion = async (templateId: string) => {
    if (!versionFile) return;
    setVersionUploading(templateId);
    const token = localStorage.getItem("kontractai_token");
    const fd = new FormData();
    fd.append("file", versionFile);
    try {
      const res = await fetch(`/api/self-service/templates/${templateId}/version`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (res.ok) { setVersionFile(null); load(); }
      else { const e = await res.json().catch(() => ({})); alert(e.error || "Upload failed"); }
    } catch (e: any) { alert("Version upload failed"); }
    setVersionUploading(null);
  };

  const reviewCategoryTypes = typesByCategory(reviewForm.agreement_category);
  const typeInReviewExists = reviewCategoryTypes.some((t: string) => t.toLowerCase() === (reviewForm.agreement_type || "").toLowerCase());

  const inp: CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box" };
  const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: T.txD, marginBottom: 4 };

  return (
    <div>
      <SectionTitle sub={`${templates.filter((t: any) => t.is_active).length} active template${templates.filter((t: any) => t.is_active).length !== 1 ? "s" : ""}`}>Self-Service Templates</SectionTitle>

      {/* Source filter (EXHIBIT_UI_W3_20260415) */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <label style={{ fontSize: 11, color: T.txD, fontWeight: 600 }}>Filter:</label>
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value as any)}
          style={{ padding: "6px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit" }}>
          <option value="all">All Templates</option>
          <option value="admin">Admin Defaults Only</option>
          <option value="counterparty">Counterparty-Specific Only</option>
        </select>
      </div>

      {/* Drag and drop zone */}
      {!pendingReview && !analyzing && (
        <Card style={{ marginBottom: 16, padding: 0 }}>
          <div onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={handleDrop}
            style={{ padding: 28, textAlign: "center", border: `2px dashed ${drag ? T.acc : T.brd}`, borderRadius: 8, backgroundColor: drag ? T.accS : "transparent", transition: "all .2s", cursor: "pointer" }}
            onClick={() => document.getElementById("ss-tmpl-input")?.click()}>
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>&#9830;</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>Drop a template file here, or click to browse</div>
            <div style={{ fontSize: 11, color: T.txD, marginTop: 4 }}>PDF or Word (.docx). AI will auto-detect the template name, agreement type, and category for your review.</div>
            <input id="ss-tmpl-input" type="file" accept=".pdf,.docx,.doc" onChange={handleFileSelect} style={{ display: "none" }} />
          </div>
        </Card>
      )}

      {/* Analyzing indicator */}
      {analyzing && (
        <Card style={{ marginBottom: 16, padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.acc, marginBottom: 6 }}>Analyzing template with AI...</div>
          <div style={{ fontSize: 12, color: T.txM }}>Extracting template name, agreement type, and category. This may take a moment.</div>
        </Card>
      )}

      {/* Pending review card */}
      {pendingReview && (
        <Card style={{ marginBottom: 16, padding: 20, border: `2px solid ${T.acc}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <Badge c={T.acc} b={T.accS}>Review Required</Badge>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>Verify AI-Extracted Template Details</span>
          </div>
          {pendingReview.ai_result && (
            <div style={{ padding: "8px 12px", backgroundColor: T.bg3, borderRadius: 6, marginBottom: 14, fontSize: 11, color: T.txM, border: `1px solid ${T.brd}` }}>
              AI detected: <strong style={{ color: T.tx }}>{pendingReview.ai_result.name}</strong> | {pendingReview.ai_result.agreement_type} | {pendingReview.ai_result.agreement_category}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={lbl}>Template Name *</label>
              <input value={reviewForm.name || ""} onChange={e => setReviewForm((p: any) => ({ ...p, name: e.target.value }))} style={inp} />
            </div>
            <div>
              <label style={lbl}>Agreement Category *</label>
              <select value={reviewForm.agreement_category || ""} onChange={e => {
                const newCat = e.target.value;
                const catTypes = typesByCategory(newCat);
                setReviewForm((p: any) => ({ ...p, agreement_category: newCat, agreement_type: catTypes.some((t: string) => t.toLowerCase() === (p.agreement_type || "").toLowerCase()) ? p.agreement_type : "" }));
                setNewTypeName("");
              }} style={inp}>
                <option value="">Select category...</option>
                <option value="Customer">Customer</option>
                <option value="Vendor">Vendor</option>
                <option value="Corporate">Corporate</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>Agreement Type * {!reviewForm.agreement_category && <span style={{ fontWeight: 400, color: T.txD }}>(select category first)</span>}</label>
            <select value={typeInReviewExists ? reviewForm.agreement_type : "__custom__"} onChange={e => {
              if (e.target.value === "__custom__") setReviewForm((p: any) => ({ ...p, agreement_type: "" }));
              else setReviewForm((p: any) => ({ ...p, agreement_type: e.target.value }));
            }} disabled={!reviewForm.agreement_category} style={{ ...inp, opacity: reviewForm.agreement_category ? 1 : 0.5 }}>
              <option value="">{reviewForm.agreement_category ? "Select type..." : "Select category first..."}</option>
              {reviewCategoryTypes.map((tp: string) => <option key={tp} value={tp}>{tp}</option>)}
              {reviewForm.agreement_category && <option value="__custom__">+ Add new contract type...</option>}
            </select>
            {(!typeInReviewExists && reviewForm.agreement_type !== "") && (
              <div style={{ marginTop: 6, padding: "8px 10px", backgroundColor: "rgba(208,64,64,.06)", border: "1px solid rgba(208,64,64,.15)", borderRadius: 6 }}>
                <div style={{ fontSize: 11, color: T.red, fontWeight: 600, marginBottom: 4 }}>"{reviewForm.agreement_type}" is not an existing contract type.</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input value={newTypeName || reviewForm.agreement_type} onChange={e => setNewTypeName(e.target.value)} placeholder="Contract type name" style={{ ...inp, flex: 1 }} />
                  <Btn v="primary" sz="sm" disabled={!(newTypeName || reviewForm.agreement_type) || !reviewForm.agreement_category || addingType} onClick={() => { if (!newTypeName) setNewTypeName(reviewForm.agreement_type); addNewType(); }}>
                    {addingType ? "Adding..." : `Add to ${reviewForm.agreement_category || "..."}`}
                  </Btn>
                </div>
                {!reviewForm.agreement_category && <div style={{ fontSize: 10, color: T.red, marginTop: 4 }}>Select a category first to add the new type.</div>}
              </div>
            )}
            {!typeInReviewExists && reviewForm.agreement_type === "" && (
              <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center" }}>
                <input value={newTypeName} onChange={e => setNewTypeName(e.target.value)} placeholder="New contract type name..." style={{ ...inp, flex: 1 }} />
                <Btn v="primary" sz="sm" disabled={!newTypeName || !reviewForm.agreement_category || addingType} onClick={() => { addNewType(); }}>
                  {addingType ? "Adding..." : `Add to ${reviewForm.agreement_category || "..."}`}
                </Btn>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Description</label>
            <textarea value={reviewForm.description || ""} onChange={e => setReviewForm((p: any) => ({ ...p, description: e.target.value }))} rows={2} style={{ ...inp, resize: "vertical" }} />
          </div>

          {/* Document preview with highlighted placeholders */}
          {pendingReview.document_text && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <label style={lbl}>Document Preview — Placeholders Highlighted</label>
                <span style={{ fontSize: 10, color: T.txD }}>{placeholderDraft.length} placeholder{placeholderDraft.length !== 1 ? "s" : ""} detected</span>
              </div>
              <div style={{ maxHeight: 300, overflow: "auto", padding: "12px 14px", backgroundColor: "#FAFBFC", border: `1px solid ${T.brd}`, borderRadius: 6, fontSize: 12, lineHeight: 1.8, color: T.tx, whiteSpace: "pre-wrap", fontFamily: "'Segoe UI', sans-serif" }}>
                {(() => {
                  let text = pendingReview.document_text;
                  if (!placeholderDraft.length) return text;
                  // Build segments with highlights
                  const finds = placeholderDraft.map(p => p.find).filter(Boolean).sort((a: any, b: any) => b.length - a.length);
                  if (!finds.length) return text;
                  const regex = new RegExp(`(${finds.map(f => f.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
                  const parts = text.split(regex);
                  return parts.map((part: any, i: number) => {
                    const match = placeholderDraft.find(p => p.find === part);
                    if (match) {
                      const fieldLabel = FIELD_OPTIONS.find(f => f.value === match.field)?.label;
                      return <span key={i} style={{ backgroundColor: match.field === "other" || !match.field ? "#FFF3CD" : "#D4EDDA", borderRadius: 3, padding: "1px 4px", border: `1px solid ${match.field === "other" || !match.field ? "#FECF6D" : "#A3D9A5"}`, cursor: "pointer" }} title={fieldLabel ? `→ ${fieldLabel}` : match.suggestion || "Unmapped — click Edit Placeholders to map"}>{part}{fieldLabel && <sup style={{ fontSize: 8, color: "#0D6B3D", marginLeft: 2 }}>{fieldLabel}</sup>}</span>;
                    }
                    return <span key={i}>{part}</span>;
                  });
                })()}
              </div>
            </div>
          )}

          {/* Placeholder field mapping */}
          <div style={{ marginBottom: 14, padding: "12px 14px", backgroundColor: T.bg3, borderRadius: 8, border: `1px solid ${T.brd}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.tx }}>Placeholder Field Mappings</div>
              <Btn v="ghost" sz="sm" onClick={() => setPlaceholderDraft(prev => [...prev, { find: "", field: "", label: "", suggestion: "" }])}>+ Add</Btn>
            </div>
            <div style={{ fontSize: 11, color: T.txD, marginBottom: 8 }}>Map each placeholder in the template to a data field. These will be auto-filled when a requester creates a self-service request.</div>
            {placeholderDraft.length === 0 && <div style={{ fontSize: 11, color: T.txD, fontStyle: "italic" }}>No placeholders detected. Add them manually if the template has fill-in fields.</div>}
            {placeholderDraft.map((pf, idx: number) => (
              <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                <input value={pf.find || ""} onChange={e => { const u = [...placeholderDraft]; u[idx] = { ...u[idx], find: e.target.value }; setPlaceholderDraft(u); }} placeholder="Exact placeholder text in document..." style={{ flex: 2, ...inp, fontSize: 11, padding: "5px 8px" }} />
                <select value={pf.field || ""} onChange={e => { const u = [...placeholderDraft]; u[idx] = { ...u[idx], field: e.target.value }; setPlaceholderDraft(u); }} style={{ flex: 1, ...inp, fontSize: 11, padding: "5px 8px" }}>
                  <option value="">Map to...</option>
                  {FIELD_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  <option value="other">Other (manual)</option>
                </select>
                {pf.field === "other" && <input value={pf.suggestion || ""} onChange={e => { const u = [...placeholderDraft]; u[idx] = { ...u[idx], suggestion: e.target.value }; setPlaceholderDraft(u); }} placeholder="What should go here?" style={{ flex: 1, ...inp, fontSize: 11, padding: "5px 8px" }} />}
                <button onClick={() => setPlaceholderDraft(prev => prev.filter((_: any, i: number) => i !== idx))} style={{ background: "none", border: "none", color: T.red, cursor: "pointer", fontSize: 14, padding: "0 4px" }}>x</button>
              </div>
            ))}
          </div>

          {placeholderError && (
            <div style={{ marginBottom: 8, padding: "6px 10px", fontSize: 11, borderRadius: 4, border: `1px solid ${T.red}`, color: T.red, backgroundColor: "#FDECEA" }}>{placeholderError}</div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn v="primary" disabled={!reviewForm.name || !reviewForm.agreement_type || !reviewForm.agreement_category || !typeInReviewExists} onClick={() => { confirmReview(); }}>Confirm & Activate Template</Btn>
            <Btn v="ghost" style={{ color: T.red }} onClick={() => { discardPending(); }}>Discard</Btn>
          </div>
          {/* A disabled button with no explanation reads as "there is no Save
              option" — which is how it was described from Certara. Name the
              condition that is unmet, every time, not only for the type. */}
          {(() => {
            const missing = [
              !reviewForm.name && "a template name",
              !reviewForm.agreement_category && "an agreement category",
              !reviewForm.agreement_type && "an agreement type",
              (reviewForm.agreement_type && !typeInReviewExists) && `the contract type "${reviewForm.agreement_type}" to exist (add it above)`,
            ].filter(Boolean);
            if (missing.length === 0) return null;
            return (
              <div style={{ fontSize: 10, color: T.txD, marginTop: 6 }}>
                Confirm &amp; Activate needs {missing.join(", ")}.
              </div>
            );
          })()}
        </Card>
      )}

      {/* Template list */}
      {(() => {
        const filteredTemplates = templates.filter((t: any) => {
          if (sourceFilter === "admin") return !t.source_contract_id;
          if (sourceFilter === "counterparty") return !!t.source_contract_id;
          return true;
        });
        if (filteredTemplates.filter((t: any) => t.is_active || expandedId === t.id).length === 0 && !pendingReview && !analyzing) {
          return (
            <Card style={{ padding: 24, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: T.txM }}>
                {sourceFilter === "all" ? "No templates configured. Drop a file above to get started." :
                 sourceFilter === "admin" ? "No admin default templates." :
                 "No counterparty-specific templates. These are created via AI Import exhibit extraction."}
              </div>
            </Card>
          );
        }
        return filteredTemplates.map((t: any) => {
        const isExpanded = expandedId === t.id;
        const isEditing = editId === t.id;
        const isAnalyzing2 = analyzing === t.id;
        const isPendingThis = pendingReview?.templateId === t.id;
        if (isPendingThis) return null; // shown in review card above

        return (
          <Card key={t.id} style={{ marginBottom: 8, padding: 0, opacity: t.is_active ? 1 : 0.5 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", cursor: "pointer" }} onClick={() => setExpandedId(isExpanded ? null : t.id)}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{t.name}</span>
                  {t.agreement_category && <Badge c={t.agreement_category === "Customer" ? T.blu : t.agreement_category === "Vendor" ? T.prp : T.orn} b={t.agreement_category === "Customer" ? T.bluS : t.agreement_category === "Vendor" ? T.prpS : T.ornS}>{t.agreement_category}</Badge>}
                  {t.is_active
                    ? <Badge c={T.grn} b={T.grnS}>Active</Badge>
                    : (t.source_contract_status === 'superseded'
                        ? <Badge c={T.txD} b={T.bg3}>Superseded</Badge>
                        : <Badge c={T.ylw} b={T.ylwS}>Inactive</Badge>)}
                  {t.source_contract_id && (() => {
                    const parent = contractsMap[t.source_contract_id];
                    const cp = parent?.counterparty_entity || "Unknown";
                    return <Badge c={T.acc} b={T.accS}>🔗 From: {cp} MSA</Badge>;
                  })()}
                  {isAnalyzing2 && <span style={{ fontSize: 10, color: T.acc, fontWeight: 600 }}>Analyzing...</span>}
                </div>
                <div style={{ fontSize: 11, color: T.txM, marginTop: 2 }}>{t.agreement_type} {t.description ? `- ${t.description}` : ""}</div>
                <NegotiationBaselineRow
                  template={t}
                  onChanged={load}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 10, color: T.txD }}>v{t.version || 1}</span>
                <span style={{ fontSize: 10, color: T.txD }}>{t.file_name}</span>
                <span style={{ fontSize: 12, color: T.txD, transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}>&#9660;</span>
              </div>
            </div>
            {t.is_negotiation_baseline && <DeltaHarvestPanel template={t} />}

            {isExpanded && (
              <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${T.brd}` }}>
                {isEditing ? (
                  <div style={{ padding: "12px 0" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                      <div><label style={lbl}>Name</label><input value={editForm.name || ""} onChange={e => setEditForm((p: any) => ({ ...p, name: e.target.value }))} style={inp} /></div>
                      <div><label style={lbl}>Category</label>
                        <select value={editForm.agreement_category || ""} onChange={e => {
                          const nc = e.target.value;
                          const ct = typesByCategory(nc);
                          setEditForm((p: any) => ({ ...p, agreement_category: nc, agreement_type: ct.some((x: string) => x.toLowerCase() === (p.agreement_type || "").toLowerCase()) ? p.agreement_type : "" }));
                        }} style={inp}>
                          <option value="">Select...</option><option value="Customer">Customer</option><option value="Vendor">Vendor</option><option value="Corporate">Corporate</option>
                        </select>
                      </div>
                      <div><label style={lbl}>Agreement Type</label>
                        <select value={editForm.agreement_type || ""} onChange={e => setEditForm((p: any) => ({ ...p, agreement_type: e.target.value }))} disabled={!editForm.agreement_category} style={{ ...inp, opacity: editForm.agreement_category ? 1 : 0.5 }}>
                          <option value="">{editForm.agreement_category ? "Select..." : "Select category first..."}</option>{typesByCategory(editForm.agreement_category).map((tp: string) => <option key={tp} value={tp}>{tp}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ marginBottom: 8 }}><label style={lbl}>Description</label>
                      <textarea value={editForm.description || ""} onChange={e => setEditForm((p: any) => ({ ...p, description: e.target.value }))} rows={2} style={{ ...inp, resize: "vertical" }} />
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn v="primary" sz="sm" onClick={() => saveEdit(t.id)}>Save</Btn>
                      <Btn v="ghost" sz="sm" onClick={() => setEditId(null)}>Cancel</Btn>
                    </div>
                  </div>
                ) : (<>
                  {t.source_contract_id && (() => {
                    const parent = contractsMap[t.source_contract_id];
                    if (!parent) return null;
                    return (
                      <div style={{ padding: "8px 12px", margin: "10px 0 6px", backgroundColor: "rgba(26,138,158,.06)", border: "1px solid rgba(26,138,158,.2)", borderRadius: 6, fontSize: 11, color: T.tx }}>
                        <span style={{ fontWeight: 600, color: "#1A8A9E" }}>🔗 Extracted from: </span>
                        <a href={`/contracts/${parent.id}`} style={{ color: T.acc, textDecoration: "none", fontWeight: 500 }}>{parent.name}</a>
                        {parent.counterparty_entity && <span style={{ color: T.txM }}> ({parent.counterparty_entity})</span>}
                      </div>
                    );
                  })()}
                  {(t.file_name?.endsWith('.docx') || t.file_name?.endsWith('.doc')) && (
                    <div style={{ display: "flex", gap: 6, padding: "8px 0 4px", flexWrap: "wrap", alignItems: "center" }}>
                      <Btn v="secondary" sz="sm" onClick={() => {
                        const spUrl = t.sharepoint_url;
                        if (spUrl) window.location.href = `ms-word:ofe|u|${spUrl}`;
                        else openInWord("template", t.id);
                      }}>Open in Word</Btn>
                      {t.sharepoint_url && <Btn v="secondary" sz="sm" onClick={() => { const folder = t.sharepoint_url.substring(0, t.sharepoint_url.lastIndexOf("/")); window.open(folder || t.sharepoint_url, "_blank"); }}>View in SharePoint</Btn>}
                      {!t.sharepoint_url && <Btn v="secondary" sz="sm" disabled={spSyncing === t.id} onClick={async () => {
                        setSpSyncing(t.id);
                        const r = await apiCall(`/self-service/templates/${t.id}/sync-sharepoint`, { method: "POST" });
                        setSpSyncing(null);
                        if (r?.sharepoint_url) load();
                        else alert(r?.error || "SharePoint sync failed");
                      }}>{spSyncing === t.id ? "Syncing..." : "Sync to SharePoint"}</Btn>}
                      {t.sharepoint_url && <span style={{ fontSize: 10, color: T.grn }}>Synced to SharePoint</span>}
                    </div>
                  )}
                  {t.file_name?.endsWith('.pdf') && (
                    <div style={{ display: "flex", gap: 6, padding: "8px 0 4px", flexWrap: "wrap", alignItems: "center" }}>
                      <Btn v="secondary" sz="sm" onClick={() => viewDocument("template", t.id)}>View PDF</Btn>
                      {t.sharepoint_url && <Btn v="secondary" sz="sm" onClick={() => window.open(t.sharepoint_url, "_blank")}>View in SharePoint</Btn>}
                      {!t.sharepoint_url && <Btn v="secondary" sz="sm" disabled={spSyncing === t.id} onClick={async () => {
                        setSpSyncing(t.id);
                        const r = await apiCall(`/self-service/templates/${t.id}/sync-sharepoint`, { method: "POST" });
                        setSpSyncing(null);
                        if (r?.sharepoint_url) load();
                        else alert(r?.error || "SharePoint sync failed");
                      }}>{spSyncing === t.id ? "Syncing..." : "Sync to SharePoint"}</Btn>}
                      {t.sharepoint_url && <span style={{ fontSize: 10, color: T.grn }}>Synced to SharePoint</span>}
                    </div>
                  )}
                  {/* Management actions */}
                  <div style={{ display: "flex", gap: 6, padding: "4px 0 6px", flexWrap: "wrap" }}>
                    <Btn v="secondary" sz="sm" onClick={() => { setEditId(t.id); setEditForm({ name: t.name, agreement_type: t.agreement_type, agreement_category: t.agreement_category || "", description: t.description || "" }); }}>Edit</Btn>
                    <Btn v="secondary" sz="sm" onClick={() => reAnalyze(t)} disabled={!!analyzing}>Re-analyze with AI</Btn>
                    <Btn v="secondary" sz="sm" onClick={() => toggleActive(t)}>{t.is_active ? "Deactivate" : "Activate"}</Btn>
                    <Btn v="secondary" sz="sm" onClick={() => { setVersionFile(null); if (versionInputRef.current) versionInputRef.current.click(); }}>Upload New Version</Btn>
                    <Btn v="ghost" sz="sm" style={{ color: T.red }} onClick={async () => { if (!window.confirm(`Permanently delete "${t.name}"? This removes the template and all version history.`)) return; await apiCall(`/self-service/templates/${t.id}`, { method: "DELETE" }); setExpandedId(null); load(); }}>Delete</Btn>
                    <input ref={versionInputRef} type="file" accept=".pdf,.docx,.doc" style={{ display: "none" }} onChange={e => { setVersionFile((e.target as HTMLInputElement).files?.[0] || null); e.target.value = ""; }} />
                    {versionFile && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 11, color: T.txM }}>{versionFile.name}</span>
                        <Btn v="primary" sz="sm" disabled={versionUploading === t.id} onClick={() => uploadVersion(t.id)}>{versionUploading === t.id ? "Uploading..." : "Confirm Upload"}</Btn>
                        <Btn v="ghost" sz="sm" onClick={() => setVersionFile(null)}>Cancel</Btn>
                      </div>
                    )}
                  </div>

                  {/* Placeholder Fields */}
                  <div style={{ marginTop: 10, padding: "10px 0", borderTop: `1px solid ${T.brd}` }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: T.txD }}>Placeholder Fields ({(t.placeholder_fields || []).length} mapped)</div>
                      {placeholderError && editingPlaceholders !== t.id && (
                        <div style={{ marginLeft: 8, padding: "2px 8px", fontSize: 10, borderRadius: 4, border: `1px solid ${T.red}`, color: T.red, backgroundColor: "#FDECEA" }}>{placeholderError}</div>
                      )}
                      <div style={{ display: "flex", gap: 4 }}>
                        <Btn v="ghost" sz="sm" disabled={scanningPlaceholders === t.id} onClick={() => scanPlaceholders(t)}>{scanningPlaceholders === t.id ? "Scanning..." : "AI Scan"}</Btn>
                        <Btn v="ghost" sz="sm" onClick={() => { setPlaceholderError(null); setEditingPlaceholders(editingPlaceholders === t.id ? null : t.id); setPlaceholderDraft(Array.isArray(t.placeholder_fields) ? t.placeholder_fields : []); }}>{editingPlaceholders === t.id ? "Cancel" : "Edit"}</Btn>
                      </div>
                    </div>
                    {editingPlaceholders === t.id ? (
                      <div>
                        {placeholderDraft.map((pf, idx: number) => (
                          <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                            <input value={pf.find || ""} onChange={e => { const u = [...placeholderDraft]; u[idx] = { ...u[idx], find: e.target.value }; setPlaceholderDraft(u); }} placeholder="Exact placeholder text..." style={{ flex: 2, ...inp, fontSize: 11, padding: "5px 8px" }} />
                            <select value={pf.field || ""} onChange={e => { const u = [...placeholderDraft]; u[idx] = { ...u[idx], field: e.target.value }; setPlaceholderDraft(u); }} style={{ flex: 1, ...inp, fontSize: 11, padding: "5px 8px" }}>
                              <option value="">Map to...</option>
                              {FIELD_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                            <input value={pf.label || ""} onChange={e => { const u = [...placeholderDraft]; u[idx] = { ...u[idx], label: e.target.value }; setPlaceholderDraft(u); }} placeholder="Label..." style={{ flex: 1, ...inp, fontSize: 11, padding: "5px 8px" }} />
                            <button onClick={() => setPlaceholderDraft(prev => prev.filter((_: any, i: number) => i !== idx))} style={{ background: "none", border: "none", color: T.red, cursor: "pointer", fontSize: 14, padding: "0 4px" }}>x</button>
                          </div>
                        ))}
                        {placeholderError && (
                          <div style={{ marginTop: 6, padding: "6px 10px", fontSize: 11, borderRadius: 4, border: `1px solid ${T.red}`, color: T.red, backgroundColor: "#FDECEA" }}>{placeholderError}</div>
                        )}
                        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                          <Btn v="ghost" sz="sm" onClick={() => setPlaceholderDraft(prev => [...prev, { find: "", field: "", label: "" }])}>+ Add Field</Btn>
                          <Btn v="primary" sz="sm" onClick={() => savePlaceholders(t.id, (t.placeholder_fields || []).length)}>Save Placeholders</Btn>
                        </div>
                      </div>
                    ) : (t.placeholder_fields || []).length > 0 ? (
                      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "2px 8px", fontSize: 11 }}>
                        <div style={{ fontWeight: 600, color: T.txD, padding: "2px 0" }}>Placeholder Text</div>
                        <div style={{ fontWeight: 600, color: T.txD, padding: "2px 0" }}>Maps To</div>
                        <div style={{ fontWeight: 600, color: T.txD, padding: "2px 0" }}>Label</div>
                        {(t.placeholder_fields || []).map((pf: any, i: number) => (<>
                          <div key={`f${i}`} style={{ color: T.tx, fontFamily: "monospace", fontSize: 10, padding: "2px 0" }}>{pf.find}</div>
                          <div key={`m${i}`} style={{ color: T.acc, padding: "2px 0" }}>{FIELD_OPTIONS.find(f => f.value === pf.field)?.label || pf.field}</div>
                          <div key={`l${i}`} style={{ color: T.txM, padding: "2px 0" }}>{pf.label}</div>
                        </>))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: T.txD }}>No placeholders defined. Click "AI Scan" to auto-detect or "Edit" to add manually.</div>
                    )}
                  </div>
                </>)}

                {t.versions && t.versions.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: T.txD, marginBottom: 4 }}>Version History</div>
                    {t.versions.map((v: any) => (
                      <div key={v.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0", fontSize: 11, borderBottom: `1px solid ${T.brdSubtle}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Badge c={v.version === t.version ? T.grn : T.txD} b={v.version === t.version ? T.grnS : T.bg4}>v{v.version}</Badge>
                          <span style={{ color: T.tx }}>{v.file_name}</span>
                        </div>
                        <div style={{ color: T.txD }}>{v.uploaded_by_name || "System"} - {v.created_at ? new Date(v.created_at).toLocaleDateString() : ""}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      });
      })()}
    </div>
  );
};

/* ═══════════════════ EXECUTION QUEUE ═══════════════════ */
